import type { FidelityTier } from '../utils/fidelity-tiers';
export type FidelityCardScan = {
  kind: 'FIDELITY_CARD';
  fidelityCardCode: string;
  fidelityCode: string;
  customerId: number;
  branchCode: string;
  historicalPoints: number;
  availablePoints: number;
  currentTier: FidelityTier | null;
  discountPercent: number;
  eligible: boolean;
  pointsDebited: 0;
  qrCodeValue: string;
};
import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import { normalizePro20Response } from '@/shared/api/pro20-transform';
import {
  fetchFidelityRewardList,
  normalizeFidelityRewardGrant,
  normalizeFidelityRewardRule,
} from './fidelity-reward-responses';

import type {
  AddPointsBasedOnAmountParams,
  AddPointsParams,
  AddPrepaidAmountParams,
  AddThresholdsParams,
  CheckPublicFidelityCustomerParams,
  CheckPublicFidelityCustomerResult,
  CreateFidelityCardParams,
  CreateFidelityConfPayload,
  DeleteFidelityConfParams,
  FidelityCard,
  FidelityCustomerDiscountCard,
  FidelityRewardGrant,
  FidelityRewardRule,
  FidelityOperation,
  FidelityCardCodeParams,
  FidelityCardIdParams,
  FidelityCardsPageParams,
  FidelityCodeParams,
  FidelityConf,
  FidelityListParams,
  PageFidelityCard,
  RegisterFidelityCardParams,
  RequestPublicFidelityLinkParams,
  RemoveThresholdParams,
  FidelityThreshold,
  UpdateFidelityConfPayload,
  UpdatePointAmountParams,
  UpdateThresholdParams,
  UseFidelityCardParams,
} from '../types/fidelity.types';
import {
  normalizeCheckPublicFidelityCustomer,
  normalizeFidelityCard,
  normalizeFidelityCardList,
  normalizeFidelityCardPage,
  normalizeFidelityConf,
  normalizeFidelityConfList,
} from '../utils/normalize-fidelity';

function buildFidelityInvalidationTags(branchCode?: string) {
  return branchCode
    ? [{ type: API_TAGS.Fidelity, id: branchCode }]
    : [{ type: API_TAGS.Fidelity, id: 'LIST' }];
}

function buildThresholdRequest(
  threshold: FidelityThreshold,
): FidelityThreshold {
  return {
    ...(threshold.code ? { code: threshold.code } : {}),
    ...(threshold.lowerBound !== undefined
      ? { lowerBound: threshold.lowerBound }
      : {}),
    ...(threshold.upperBound !== undefined
      ? { upperBound: threshold.upperBound }
      : {}),
    ...(threshold.points !== undefined ? { points: threshold.points } : {}),
    ...(threshold.hexColor ? { hexColor: threshold.hexColor } : {}),
  };
}

function buildFidelityListUrl({
  branchCode,
  page = 0,
  size = 25,
  search,
  sortBy = 'fidelityName',
  sortDirection = 'asc',
}: FidelityListParams): string {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('size', String(size));
  params.set('search', search?.trim() ?? '');
  params.set('sortBy', sortBy);
  params.set('sortDirection', sortDirection);
  return `/fidelity/retrievebybranchcode/${branchCode}?${params.toString()}`;
}

export const fidelityApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getFidelityConfsByBranch: builder.query<FidelityConf[], FidelityListParams>(
      {
        query: (args) => ({
          url: buildFidelityListUrl(args),
          method: 'GET',
        }),
        transformResponse: (response: unknown) =>
          normalizeFidelityConfList(normalizePro20Response(response)),
        providesTags: (_result, _error, { branchCode }) => [
          { type: API_TAGS.Fidelity, id: branchCode },
        ],
      },
    ),

    getFidelityProgramPage: builder.query<
      { content: FidelityConf[]; totalElements: number; totalPages: number },
      FidelityListParams
    >({
      query: (args) => ({ url: buildFidelityListUrl(args), method: 'GET' }),
      transformResponse: (response: unknown) => {
        const value = normalizePro20Response(response) as {
          totalElements?: number;
          totalPages?: number;
        };
        const content = normalizeFidelityConfList(value);
        return {
          content,
          totalElements: value.totalElements ?? content.length,
          totalPages: value.totalPages ?? 1,
        };
      },
      providesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Fidelity, id: branchCode },
      ],
    }),
    setFidelityProgramActive: builder.mutation<
      unknown,
      {
        fidelityId: number;
        fidelityCode: string;
        branchCode: string;
        active: boolean;
      }
    >({
      query: (body) => ({ url: '/fidelity/updateconf', method: 'PUT', body }),
      transformResponse: (response: unknown) =>
        normalizePro20Response(response),
      invalidatesTags: (_result, error, { branchCode, fidelityCode }) =>
        error
          ? []
          : [
              { type: API_TAGS.Fidelity, id: branchCode },
              { type: API_TAGS.Fidelity, id: fidelityCode },
            ],
    }),

    getFidelityConfByCode: builder.query<FidelityConf, FidelityCodeParams>({
      query: ({ fidelityCode }) => ({
        url: `/fidelity/retrieve/${fidelityCode}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeFidelityConf(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid fidelity configuration response');
        }
        return normalized;
      },
      providesTags: (result) =>
        result
          ? [
              { type: API_TAGS.Fidelity, id: result.branchCode },
              { type: API_TAGS.Fidelity, id: result.fidelityCode },
            ]
          : [],
    }),

    createFidelityConf: builder.mutation<
      FidelityConf,
      CreateFidelityConfPayload
    >({
      query: (body) => ({
        url: '/fidelity/create',
        method: 'POST',
        body: {
          ...body,
          ...(body.thresholds
            ? { thresholds: body.thresholds.map(buildThresholdRequest) }
            : {}),
        },
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeFidelityConf(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid create fidelity response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { branchCode }) =>
        buildFidelityInvalidationTags(branchCode),
    }),

    updateFidelityConf: builder.mutation<
      FidelityConf,
      UpdateFidelityConfPayload
    >({
      query: (body) => ({
        url: '/fidelity/updateconf',
        method: 'PUT',
        body: {
          ...body,
          ...(body.thresholds
            ? { thresholds: body.thresholds.map(buildThresholdRequest) }
            : {}),
        },
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeFidelityConf(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid update fidelity response');
        }
        return normalized;
      },
      invalidatesTags: (result) =>
        result
          ? [
              { type: API_TAGS.Fidelity, id: result.branchCode },
              { type: API_TAGS.Fidelity, id: result.fidelityCode },
              {
                type: API_TAGS.Fidelity,
                id: `rewards-${String(result.fidelityId)}`,
              },
            ]
          : [],
    }),

    deleteFidelityConf: builder.mutation<unknown, DeleteFidelityConfParams>({
      query: ({ fidelityConfId }) => ({
        url: `/fidelity/delete/${String(fidelityConfId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { branchCode }) =>
        buildFidelityInvalidationTags(branchCode),
    }),

    duplicateFidelityConf: builder.mutation<
      FidelityConf,
      FidelityCodeParams & { branchCode?: string }
    >({
      query: ({ fidelityCode }) => ({
        url: `/fidelity/duplicate/${fidelityCode}`,
        method: 'PUT',
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeFidelityConf(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid duplicate fidelity response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { branchCode }) =>
        buildFidelityInvalidationTags(branchCode),
    }),

    // Threshold mutations intentionally skip invalidatesTags so the edit-page
    // can close create/edit/delete modals first, then refresh via refetch.
    addFidelityThresholds: builder.mutation<FidelityConf, AddThresholdsParams>({
      query: ({ fidelityConfId, thresholds }) => ({
        url: `/fidelity/${String(fidelityConfId)}/thresholds`,
        method: 'POST',
        body: thresholds.map(buildThresholdRequest),
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeFidelityConf(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid add thresholds response');
        }
        return normalized;
      },
    }),

    updateFidelityThreshold: builder.mutation<
      FidelityConf,
      UpdateThresholdParams
    >({
      query: ({ fidelityConfId, threshold }) => ({
        url: `/fidelity/updatethreshold/${String(fidelityConfId)}`,
        method: 'PUT',
        body: buildThresholdRequest(threshold),
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeFidelityConf(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid update threshold response');
        }
        return normalized;
      },
    }),

    removeFidelityThreshold: builder.mutation<
      FidelityConf,
      RemoveThresholdParams
    >({
      query: ({ fidelityConfId, code }) => ({
        url: `/fidelity/${String(fidelityConfId)}/thresholds/${code}`,
        method: 'DELETE',
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeFidelityConf(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid remove threshold response');
        }
        return normalized;
      },
    }),

    createFidelityCard: builder.mutation<
      FidelityCard,
      CreateFidelityCardParams
    >({
      query: ({ customerId, fidelityCode, customerDTO }) => ({
        url: `/fidelity/customer/${String(customerId)}/create/${fidelityCode}`,
        method: 'POST',
        body: customerDTO ? { customerDTO } : {},
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeFidelityCard(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid create fidelity card response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { fidelityCode }) => [
        { type: API_TAGS.Fidelity, id: fidelityCode },
      ],
    }),

    getFidelityCardsByCode: builder.query<
      PageFidelityCard,
      FidelityCardsPageParams
    >({
      query: ({ fidelityCode, page, size }) => ({
        url: `/fidelity/retrieve/${fidelityCode}/cards`,
        method: 'GET',
        params: {
          page: page ?? 0,
          size: size ?? 10,
        },
      }),
      transformResponse: (response: unknown) =>
        normalizeFidelityCardPage(normalizePro20Response(response)),
      providesTags: (_result, _error, { fidelityCode }) => [
        { type: API_TAGS.Fidelity, id: fidelityCode },
      ],
    }),

    getFidelityCardsListByCode: builder.query<
      FidelityCard[],
      FidelityCodeParams
    >({
      query: ({ fidelityCode }) => ({
        url: `/fidelity/cards/${fidelityCode}/list`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        normalizeFidelityCardList(normalizePro20Response(response)),
      providesTags: (_result, _error, { fidelityCode }) => [
        { type: API_TAGS.Fidelity, id: fidelityCode },
      ],
    }),

    getFidelityCardsByFidelityCode: builder.query<
      FidelityCard[],
      FidelityCodeParams
    >({
      query: ({ fidelityCode }) => ({
        url: `/fidelity/card/${fidelityCode}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        normalizeFidelityCardList(normalizePro20Response(response)),
      providesTags: (_result, _error, { fidelityCode }) => [
        { type: API_TAGS.Fidelity, id: fidelityCode },
      ],
    }),

    getFidelityCardById: builder.query<FidelityCard, FidelityCardIdParams>({
      query: ({ fidelityCardId }) => ({
        url: `/fidelity/card/cardid/${String(fidelityCardId)}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeFidelityCard(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid fidelity card response');
        }
        return normalized;
      },
    }),

    getFidelityCardByCode: builder.query<FidelityCard, FidelityCardCodeParams>({
      query: ({ fidelityCardCode }) => ({
        url: `/fidelity/card/cardcode/${fidelityCardCode}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeFidelityCard(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid fidelity card response');
        }
        return normalized;
      },
    }),

    getFidelityRewardRules: builder.query<
      FidelityRewardRule[],
      { fidelityId: number }
    >({
      queryFn: ({ fidelityId }, _api, _options, fetchPage) =>
        fetchFidelityRewardList(
          `/fidelity/reward-rules?fidelityId=${String(fidelityId)}`,
          fetchPage,
          normalizeFidelityRewardRule,
        ),
      providesTags: (_result, _error, { fidelityId }) => [
        { type: API_TAGS.Fidelity, id: `rewards-${String(fidelityId)}` },
      ],
    }),

    getFidelityRewardGrants: builder.query<
      FidelityRewardGrant[],
      FidelityCardCodeParams
    >({
      queryFn: ({ fidelityCardCode }, _api, _options, fetchPage) =>
        fetchFidelityRewardList(
          `/fidelity/reward-grants?fidelityCardCode=${encodeURIComponent(fidelityCardCode)}`,
          fetchPage,
          normalizeFidelityRewardGrant,
        ),
      providesTags: (_result, _error, { fidelityCardCode }) => [
        { type: API_TAGS.Fidelity, id: `grants-${fidelityCardCode}` },
      ],
    }),

    getFidelityCardHistory: builder.query<
      FidelityOperation[],
      FidelityCardCodeParams
    >({
      query: ({ fidelityCardCode }) => ({
        url: `/fidelity/card/${encodeURIComponent(fidelityCardCode)}/history`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => {
        const value = normalizePro20Response(response);
        return Array.isArray(value) ? (value as FidelityOperation[]) : [];
      },
      providesTags: (_result, _error, { fidelityCardCode }) => [
        { type: API_TAGS.Fidelity, id: `history-${fidelityCardCode}` },
      ],
    }),

    createFidelityRewardGrant: builder.mutation<
      FidelityRewardGrant,
      { fidelityCardCode: string; rewardRuleId: string }
    >({
      query: (body) => ({
        url: '/fidelity/reward-grants',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) =>
        normalizeFidelityRewardGrant(normalizePro20Response(response)),
      invalidatesTags: (_result, _error, { fidelityCardCode }) => [
        { type: API_TAGS.Fidelity, id: `grants-${fidelityCardCode}` },
      ],
    }),

    redeemFidelityRewardGrant: builder.mutation<
      FidelityRewardGrant,
      { grantId: string; redeemedBy?: string; fidelityCardCode: string }
    >({
      query: ({ grantId, redeemedBy }) => ({
        url: `/fidelity/reward-grants/${encodeURIComponent(grantId)}/redeem`,
        method: 'POST',
        body: redeemedBy ? { redeemedBy } : {},
      }),
      transformResponse: (response: unknown) =>
        normalizeFidelityRewardGrant(normalizePro20Response(response)),
      invalidatesTags: (_result, _error, { fidelityCardCode }) => [
        { type: API_TAGS.Fidelity, id: `grants-${fidelityCardCode}` },
        { type: API_TAGS.Fidelity, id: `history-${fidelityCardCode}` },
        {
          type: API_TAGS.Fidelity,
          id: `customer-discounts-${fidelityCardCode}`,
        },
      ],
    }),

    scanFidelityCard: builder.mutation<
      FidelityCardScan,
      { qrCodeValue: string; branchCode: string }
    >({
      query: (body) => ({ url: '/fidelity/cards/scan', method: 'POST', body }),
      transformResponse: (raw: unknown) =>
        normalizePro20Response(raw) as FidelityCardScan,
    }),
    scanFidelityRewardQr: builder.mutation<
      FidelityRewardGrant | FidelityCustomerDiscountCard | FidelityCardScan,
      {
        qrCodeValue: string;
        branchCode: string;
        redeemedBy: string;
        requestId: string;
      }
    >({
      query: ({ qrCodeValue, branchCode, redeemedBy, requestId }) => ({
        url: '/fidelity/reward-grants/scan',
        method: 'POST',
        body: { qrCodeValue, branchCode, redeemedBy, requestId },
      }),
      transformResponse: (response: unknown) =>
        normalizePro20Response(response) as
          FidelityRewardGrant | FidelityCustomerDiscountCard | FidelityCardScan,
      invalidatesTags: [{ type: API_TAGS.Fidelity, id: 'LIST' }],
    }),

    deleteFidelityCard: builder.mutation<
      unknown,
      { fidelityCardCode: string; fidelityCode: string }
    >({
      query: ({ fidelityCardCode }) => ({
        url: `/fidelity/cards/${encodeURIComponent(fidelityCardCode)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (
        _result,
        _error,
        { fidelityCode, fidelityCardCode },
      ) => [
        { type: API_TAGS.Fidelity, id: 'LIST' },
        { type: API_TAGS.Fidelity, id: fidelityCode },
        { type: API_TAGS.Fidelity, id: `history-${fidelityCardCode}` },
      ],
    }),

    toggleFidelityCardUsed: builder.mutation<
      FidelityCard,
      FidelityCardCodeParams
    >({
      query: ({ fidelityCardCode }) => ({
        url: `/fidelity/used/${fidelityCardCode}/toggle`,
        method: 'PUT',
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeFidelityCard(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid toggle fidelity card response');
        }
        return normalized;
      },
    }),

    useFidelityCard: builder.mutation<FidelityCard, UseFidelityCardParams>({
      query: ({ fidelityCardCode, processedBy }) => ({
        url: `/fidelity/use/${fidelityCardCode}`,
        method: 'PUT',
        params: processedBy ? { processedBy } : undefined,
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeFidelityCard(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid use fidelity card response');
        }
        return normalized;
      },
    }),

    updateFidelityPointAmount: builder.mutation<
      FidelityCard,
      UpdatePointAmountParams
    >({
      query: ({ fidelityCardCode, pointsAmount, processedBy }) => ({
        url: `/fidelity/update/${fidelityCardCode}/pointamount/${String(pointsAmount)}`,
        method: 'PUT',
        params: { processedBy },
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeFidelityCard(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid update point amount response');
        }
        return normalized;
      },
    }),

    addFidelityPrepaidAmount: builder.mutation<
      FidelityCard,
      AddPrepaidAmountParams
    >({
      query: ({ fidelityCardCode, amount }) => ({
        url: `/fidelity/prepaidamount/${fidelityCardCode}/amount/${String(amount)}`,
        method: 'PUT',
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeFidelityCard(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid add prepaid amount response');
        }
        return normalized;
      },
    }),

    addPointsToFidelityCard: builder.mutation<FidelityCard, AddPointsParams>({
      query: ({ fidelityCardCode, pointsToAdd }) => ({
        url: `/fidelity/addpoints/${fidelityCardCode}/points/${String(pointsToAdd)}`,
        method: 'PUT',
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeFidelityCard(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid add points response');
        }
        return normalized;
      },
    }),

    addPointsBasedOnAmount: builder.mutation<
      FidelityCard,
      AddPointsBasedOnAmountParams
    >({
      query: ({ fidelityCardCode, amount, processedBy }) => ({
        url: `/fidelity/addpoints/${fidelityCardCode}/amount/${String(amount)}`,
        method: 'PUT',
        params: { processedBy },
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeFidelityCard(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid add points based on amount response');
        }
        return normalized;
      },
    }),

    getPublicFidelityConf: builder.query<FidelityConf, FidelityCodeParams>({
      query: ({ fidelityCode }) => ({
        url: `/fidelity/public/${encodeURIComponent(fidelityCode)}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeFidelityConf(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid public fidelity response');
        }
        return normalized;
      },
    }),

    checkPublicFidelityCustomer: builder.mutation<
      CheckPublicFidelityCustomerResult,
      CheckPublicFidelityCustomerParams
    >({
      query: ({ fidelityCode, prefix, phone }) => ({
        url: `/fidelity/public/${encodeURIComponent(fidelityCode)}/check`,
        method: 'POST',
        body: { prefix, phone },
      }),
      transformResponse: (response: unknown) =>
        normalizeCheckPublicFidelityCustomer(normalizePro20Response(response)),
    }),

    requestPublicFidelityLink: builder.mutation<
      unknown,
      RequestPublicFidelityLinkParams
    >({
      query: ({ fidelityCode, prefix, phone }) => ({
        url: `/fidelity/public/${encodeURIComponent(fidelityCode)}/resend-link`,
        method: 'POST',
        body: { prefix, phone },
      }),
    }),

    registerPublicFidelityCard: builder.mutation<
      FidelityCard,
      RegisterFidelityCardParams
    >({
      query: ({ fidelityCode, customer }) => ({
        url: `/fidelity/public/${encodeURIComponent(fidelityCode)}/register`,
        method: 'POST',
        body: customer,
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeFidelityCard(
          normalizePro20Response(response),
        );
        if (!normalized) {
          throw new Error('Invalid fidelity register response');
        }
        return normalized;
      },
    }),
  }),
});

export const {
  useGetFidelityConfsByBranchQuery,
  useGetFidelityProgramPageQuery,
  useSetFidelityProgramActiveMutation,
  useGetFidelityConfByCodeQuery,
  useLazyGetFidelityConfByCodeQuery,
  useCreateFidelityConfMutation,
  useUpdateFidelityConfMutation,
  useDeleteFidelityConfMutation,
  useDuplicateFidelityConfMutation,
  useAddFidelityThresholdsMutation,
  useUpdateFidelityThresholdMutation,
  useRemoveFidelityThresholdMutation,
  useCreateFidelityCardMutation,
  useGetFidelityCardsByCodeQuery,
  useGetFidelityCardsListByCodeQuery,
  useGetFidelityCardsByFidelityCodeQuery,
  useGetFidelityCardByIdQuery,
  useGetFidelityCardByCodeQuery,
  useGetFidelityRewardRulesQuery,
  useGetFidelityRewardGrantsQuery,
  useGetFidelityCardHistoryQuery,
  useCreateFidelityRewardGrantMutation,
  useRedeemFidelityRewardGrantMutation,
  useScanFidelityCardMutation,
  useScanFidelityRewardQrMutation,
  useDeleteFidelityCardMutation,
  useToggleFidelityCardUsedMutation,
  useUseFidelityCardMutation,
  useUpdateFidelityPointAmountMutation,
  useAddFidelityPrepaidAmountMutation,
  useAddPointsToFidelityCardMutation,
  useAddPointsBasedOnAmountMutation,
  useGetPublicFidelityConfQuery,
  useCheckPublicFidelityCustomerMutation,
  useRequestPublicFidelityLinkMutation,
  useRegisterPublicFidelityCardMutation,
} = fidelityApi;
